/**
 * Shared OAuth2 / OIDC authorization-code callback handler.
 *
 * Validates `state`, exchanges the authorization `code` + PKCE `code_verifier`
 * for tokens, persists them, initializes the AGMS axios instance, and clears
 * the temporary PKCE artifacts.
 *
 * Used both by:
 *   - the dedicated `/auth/callback` route loader, and
 *   - the root loader (when `redirect_uri` is `/` and the AuthProvider
 *     redirects the user back to the app root with `?code=...&state=...`).
 *
 * Returns `true` if a callback was processed, `false` if the URL did not
 * contain an authorization code.
 */
import { StorageKey } from '../enum/StorageKey';
import { exchangeCodeForTokens } from '../service/auth-api';
import { OAUTH_PARAM, SESSION_STORAGE_KEY } from '../constant/auth-constants';

import { getItemFromLocalStorage, removeItemFromLocalStorage } from './local-storage';

import { persistTokenResponse } from './auth';

const clearOAuthParams = (url: URL): void => {
  [
    OAUTH_PARAM.CODE,
    OAUTH_PARAM.STATE,
    OAUTH_PARAM.SCOPE,
    OAUTH_PARAM.SESSION_STATE,
    OAUTH_PARAM.ISS,
    OAUTH_PARAM.ERROR,
    OAUTH_PARAM.ERROR_DESCRIPTION,
  ].forEach((param) => url.searchParams.delete(param));

  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
};

const getStoredCallbackCode = (): string | null => sessionStorage.getItem(SESSION_STORAGE_KEY.CALLBACK_CODE);
const setStoredCallbackCode = (code: string): void => sessionStorage.setItem(SESSION_STORAGE_KEY.CALLBACK_CODE, code);

export const handleAuthorizationCallback = async (): Promise<boolean> => {
  // Prevent duplicate processing of the same one-time authorization code
  // if the user refreshes the browser while the callback is still executing.
  //
  // We persist the last-seen `code` in `sessionStorage` (per-tab). This
  // ensures the guard only applies to the current tab and automatically
  // clears when the tab is closed — appropriate for a short-lived flag.
  const url = new URL(window.location.href);
  const code = url.searchParams.get(OAUTH_PARAM.CODE);
  const state = url.searchParams.get(OAUTH_PARAM.STATE);
  const errorParam = url.searchParams.get(OAUTH_PARAM.ERROR);
  const errorDescription = url.searchParams.get(OAUTH_PARAM.ERROR_DESCRIPTION);

  if (errorParam) {
    console.error('OIDC error:', errorParam, errorDescription);
    clearOAuthParams(url);
    return false;
  }

  if (!code) {
    return false;
  }

  const storedCode = getStoredCallbackCode();
  if (storedCode === code) {
    clearOAuthParams(url);
    return false;
  }

  setStoredCallbackCode(code);
  clearOAuthParams(url);

  const expectedState = getItemFromLocalStorage(StorageKey.OAUTH_STATE);

  if (!expectedState || expectedState !== state) {
    removeItemFromLocalStorage(StorageKey.PKCE_VERIFIER);
    removeItemFromLocalStorage(StorageKey.OAUTH_STATE);

    throw new Error('OAuth state mismatch — possible CSRF attempt.');
  }

  const verifier = getItemFromLocalStorage(StorageKey.PKCE_VERIFIER);
  if (!verifier) {
    removeItemFromLocalStorage(StorageKey.PKCE_VERIFIER);
    removeItemFromLocalStorage(StorageKey.OAUTH_STATE);

    throw new Error('Missing PKCE verifier — login flow not initiated by this client.');
  }

  try {
    const tokens = await exchangeCodeForTokens({ code, codeVerifier: verifier });
    persistTokenResponse(tokens);

    return true;
  } finally {
    removeItemFromLocalStorage(StorageKey.PKCE_VERIFIER);
    removeItemFromLocalStorage(StorageKey.OAUTH_STATE);
  }
};
